import React, { useState, useEffect, useRef, useLayoutEffect, Suspense } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, Text3D } from "@react-three/drei";
import * as THREE from "three";

// === Importar todas tus fuentes ===
import SuperVanilla from "../../../fonts/Super Vanilla_Regular.json";
import SpookyCartoon from "../../../fonts/Spooky Cartoon_Regular.json";
import KindDaily from "../../../fonts/Kind Daily_Regular.json";
import CandyBeans from "../../../fonts/Candy Beans_Regular.json";
import LastShuriken from "../../../fonts/The Last Shuriken_Regular.json";
import TransformersMovie from "../../../fonts/Transformers Movie_Regular.json";
import ZombieZone from "../../../fonts/Zombie Zone_Regular.json";
import ToastyMilk from "../../../fonts/Toasty Milk_Regular.json";
import Karate from "../../../fonts/Karate_Medium.json";
import SimplyRounded from "../../../fonts/Simply_Rounded_Bold.json";
import HaloDek from "../../../fonts/Halo Dek_Regular.json";
import SundayMidnight from "../../../fonts/Sunday Midnight_Regular.json";
import LovelyHeart from "../../../fonts/LOVELY HEART_Regular.json";
import MeowInLove from "../../../fonts/Meow in Love Demo_Regular.json";
import MaximumVoltage from "../../../fonts/Maximum Voltage_Regular.json";
import MaximumVoltageItalic from "../../../fonts/Maximum Voltage_Italic.json";
import VampireWars from "../../../fonts/Vampire Wars_Regular.json";
import VampireWarsItalic from "../../../fonts/Vampire Wars_Italic.json";
import Gimbot from "../../../fonts/Gimbot_Regular.json";
import BillyBounce from "../../../fonts/Billy Bounce_Regular.json";
import BlackCastleMF from "../../../fonts/BlackCastleMF_Regular.json";
import CandyShop from "../../../fonts/Candy Shop_Regular.json";
import CandyShopBlack from "../../../fonts/Candy Shop Black_Regular.json";
import AngryBirds from "../../../fonts/AngryBirds_Regular.json";
import Tomcat from "../../../fonts/Tomcat_Regular.json";
import Comicoon from "../../../fonts/Comicoon_Regular.json";
import Grinched from "../../../fonts/Grinched_Regular.json";
import Kraash from "../../../fonts/Kraash_Regular.json";
import KraashBlack from "../../../fonts/Kraash Black_Regular.json";
import Powerful from "../../../fonts/Powerful_Regular.json";
import MilkyNice from "../../../fonts/Milky Nice_Regular.json";
import MilkyNiceClean from "../../../fonts/Milky Nice Clean_Regular.json";
import SuperMario256 from "../../../fonts/Super Mario 256_Regular.json";
import Dinosaur from "../../../fonts/Dinosaur_Regular.json";
import SealightWhisper from "../../../fonts/Sealight Whisper_Regular.json";
import Yourmate from "../../../fonts/Yourmate_Regular.json";
import BubbleGum from "../../../fonts/BubbleGum_Regular.json";
import MagicSound from "../../../fonts/Magic Sound_Regular.json";
import Bestime from "../../../fonts/Bestime_Regular.json";
import SuperBoys from "../../../fonts/Super Boys_Regular.json";
import MilkshakeCreps from "../../../fonts/Milkshake Creps_Regular.json";
import GrindAndDeath from "../../../fonts/GrindAndDeath_Demo_Regular.json";
import GodOfWar from "../../../fonts/GodOfWar_Regular.json";
import Dynamix from "../../../fonts/Dynamix_Regular.json";

const fuentes = {
  "Super Vanilla": SuperVanilla,
  "Spooky Cartoon": SpookyCartoon,
  "Kind Daily": KindDaily,
  "Candy Beans": CandyBeans,
  "The Last Shuriken": LastShuriken,
  "Transformers Movie": TransformersMovie,
  "Zombie Zone": ZombieZone,
  "Toasty Milk": ToastyMilk,
  "Karate": Karate,
  "Simply Rounded": SimplyRounded,
  "Halo Dek": HaloDek,
  "Sunday Midnight": SundayMidnight,
  "Lovely Heart": LovelyHeart,
  "Meow In Love": MeowInLove,
  "Maximum Voltage": MaximumVoltage,
  "Maximum Voltage Italic": MaximumVoltageItalic,
  "Vampire Wars": VampireWars,
  "Vampire Wars Italic": VampireWarsItalic,
  "Gimbot": Gimbot,
  "Billy Bounce": BillyBounce,
  "BlackCastleMF": BlackCastleMF,
  "Candy Shop": CandyShop,
  "Candy Shop Black": CandyShopBlack,
  "Angry Birds": AngryBirds,
  "Tomcat": Tomcat,
  "Comicoon": Comicoon,
  "Grinched": Grinched,
  "Kraash": Kraash,
  "Kraash Black": KraashBlack,
  "Powerful": Powerful,
  "Milky Nice": MilkyNice,
  "Milky Nice Clean": MilkyNiceClean,
  "Super Mario 256": SuperMario256,
  "Dinosaur": Dinosaur,
  "Sealight Whisper": SealightWhisper,
  "Yourmate": Yourmate,
  "Bubble Gum": BubbleGum,
  "Magic Sound": MagicSound,
  "Bestime": Bestime,
  "Super Boys": SuperBoys,
  "Milkshake Creps": MilkshakeCreps,
  "Grind And Death": GrindAndDeath,
  "God Of War": GodOfWar,
  "Dynamix": Dynamix,
};

function CameraController({ zoom }) {
  const { camera } = useThree();
  useEffect(() => {
    camera.position.z = zoom;
  }, [zoom, camera]);
  return null;
}

/**
 * BendableText (mejorado)
 * - curvatureH: curvatura horizontal (eje X -> arco en Z)
 * - curvatureV: curvatura vertical (eje Y -> arco en Z)
 * Guardamos la copia original por geometry.uuid para que cambios de 'height' creen una copia nueva
 */
function BendableText({
  children,
  font,
  size = 3,
  height = 1.5,
  curvatureH = 0,
  curvatureV = 0,
  position = [0, 0, 0],
  color = "#00b894",
  ...rest
}) {
  const meshRef = useRef();
  const originalByGeom = useRef({});

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const geom = mesh.geometry;
    if (!geom || !geom.attributes || !geom.attributes.position) return;

    // atributos
    const posAttr = geom.attributes.position;
    const positions = posAttr.array;
    const geomId = geom.uuid;

    // Si no hay copia para esta geometría, crearla
    if (!originalByGeom.current[geomId] || originalByGeom.current[geomId].length !== positions.length) {
      originalByGeom.current[geomId] = positions.slice(0);
    }
    const orig = originalByGeom.current[geomId];

    // Si ambas curvaturas ~ 0, restaurar originales y salir
    if (Math.abs(curvatureH) < 1e-4 && Math.abs(curvatureV) < 1e-4) {
      for (let i = 0; i < positions.length; i++) positions[i] = orig[i];
      posAttr.needsUpdate = true;
      geom.computeBoundingBox();
      geom.computeVertexNormals();
      return;
    }

    // asegurar bbox actualizado
    geom.computeBoundingBox();
    const bbox = geom.boundingBox;

    // --- Parámetros horizontales (X -> Z) ---
    const minX = bbox.min.x;
    const maxX = bbox.max.x;
    const width = maxX - minX || 1;
    const centerX = (minX + maxX) / 2;
    const totalAngleH = curvatureH * Math.PI; // -PI..PI
    const absAngleH = Math.abs(totalAngleH) < 1e-6 ? 1e-6 : totalAngleH;
    const RH = Math.abs(totalAngleH) < 1e-6 ? Infinity : width / Math.abs(totalAngleH);

    // --- Parámetros verticales (Y -> Z) ---
    const minY = bbox.min.y;
    const maxY = bbox.max.y;
    const heightY = maxY - minY || 1;
    const centerY = (minY + maxY) / 2;
    const totalAngleV = curvatureV * Math.PI; // -PI..PI
    const absAngleV = Math.abs(totalAngleV) < 1e-6 ? 1e-6 : totalAngleV;
    const RV = Math.abs(totalAngleV) < 1e-6 ? Infinity : heightY / Math.abs(totalAngleV);

    // Recorremos vértices y aplicamos primero curvatura horizontal y luego vertical
    const vertexCount = positions.length / 3;
    for (let i = 0; i < vertexCount; i++) {
      const ix = i * 3;
      const x0 = orig[ix];
      const y0 = orig[ix + 1];
      const z0 = orig[ix + 2];

      // --- Horizontal (X -> Z) ---
      let x1 = x0;
      let y1 = y0;
      let z1 = z0;
      if (Math.abs(curvatureH) >= 1e-6 && isFinite(RH)) {
        const relX = x0 - centerX;
        const thetaH = (relX / width) * totalAngleH;
        x1 = Math.sin(thetaH) * RH;
        z1 = RH - Math.cos(thetaH) * RH + z0; // sumamos z0 (extrusión local)
      }

      // --- Vertical (Y -> Z) applied on top of previous z ---
// --- Vertical (Y -> Z) con punto fijo en la base inferior ---
// --- Curvatura vertical (Y -> Z) centrada en la extrusión ---
let y2 = y1;
let z2 = z1;
if (Math.abs(curvatureV) >= 1e-6 && isFinite(RV)) {
  // Calculamos la coordenada local Z respecto al centro del espesor (extrusión)
  const bboxZ = geom.boundingBox;
  const minZ = bboxZ.min.z;
  const maxZ = bboxZ.max.z;
  const centerZ = (minZ + maxZ) / 2; // eje central del volumen
  const relZ = z0 - centerZ;

  // Relación vertical (posición Y relativa al centro del texto)
  const relY = y0 - centerY;
  const thetaV = (relY / heightY) * totalAngleV;

  // Mantiene las caras frontal y trasera planas (rotamos la extrusión respecto a su centro)
  const cosT = Math.cos(thetaV);
  const sinT = Math.sin(thetaV);
  y2 = relY;
  z2 = relZ * cosT - RV * sinT + RV + centerZ + z1 - z0;
}



      positions[ix] = x1;
      positions[ix + 1] = y2;
      positions[ix + 2] = z2;
    }

    posAttr.needsUpdate = true;
    geom.computeVertexNormals();
  }, [children, font, size, height, curvatureH, curvatureV]);

  return (
    <Text3D
      ref={meshRef}
      font={font}
      size={size}
      height={height}
      bevelEnabled
      bevelThickness={0.1}
      bevelSize={0.05}
      position={position}
      {...rest}
    >
      {children}
      <meshStandardMaterial color={color} />
    </Text3D>
  );
}

export default function Nombre3D() {
  const [texto1, setTexto1] = useState("");
  const [texto2, setTexto2] = useState("");
  const [fuente1, setFuente1] = useState("Zombie Zone");
  const [fuente2, setFuente2] = useState("Candy Beans");
  const [zoom, setZoom] = useState(40);
  const [rotacionY, setRotacionY] = useState(0);
  const [ancho, setAncho] = useState(500);
  const [alto, setAlto] = useState(400);

  // Extrusión
  const [extrusion1, setExtrusion1] = useState(1.5);
  const [extrusion2, setExtrusion2] = useState(1.5);

  // Curvaturas: Horizontal (X->Z) y Vertical (Y->Z)
  const [curvaturaH1, setCurvaturaH1] = useState(0);
  const [curvaturaV1, setCurvaturaV1] = useState(0);
  const [curvaturaH2, setCurvaturaH2] = useState(0);
  const [curvaturaV2, setCurvaturaV2] = useState(0);

  // Cargar valores previos
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("nombre3d_config")) || {};
    if (typeof saved.zoom === "number") setZoom(saved.zoom);
    if (typeof saved.rotacionY === "number") setRotacionY(saved.rotacionY);
    if (typeof saved.ancho === "number") setAncho(saved.ancho);
    if (typeof saved.alto === "number") setAlto(saved.alto);
    if (typeof saved.extrusion1 === "number") setExtrusion1(saved.extrusion1);
    if (typeof saved.extrusion2 === "number") setExtrusion2(saved.extrusion2);
    if (typeof saved.curvaturaH1 === "number") setCurvaturaH1(saved.curvaturaH1);
    if (typeof saved.curvaturaV1 === "number") setCurvaturaV1(saved.curvaturaV1);
    if (typeof saved.curvaturaH2 === "number") setCurvaturaH2(saved.curvaturaH2);
    if (typeof saved.curvaturaV2 === "number") setCurvaturaV2(saved.curvaturaV2);
  }, []);

  // Guardar configuración en localStorage
  useEffect(() => {
    localStorage.setItem(
      "nombre3d_config",
      JSON.stringify({
        zoom,
        rotacionY,
        ancho,
        alto,
        extrusion1,
        extrusion2,
        curvaturaH1,
        curvaturaV1,
        curvaturaH2,
        curvaturaV2,
      })
    );
  }, [zoom, rotacionY, ancho, alto, extrusion1, extrusion2, curvaturaH1, curvaturaV1, curvaturaH2, curvaturaV2]);

  const mostrarAmbos = texto1 && texto2;

  return (
    <div
      style={{
        background: "#fff",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: 20,
      }}
    >
      {/* Inputs de texto */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
        {[
          {
            texto: texto1,
            setTexto: setTexto1,
            fuente: fuente1,
            setFuente: setFuente1,
            extrusion: extrusion1,
            setExtrusion: setExtrusion1,
            curvH: curvaturaH1,
            setCurvH: setCurvaturaH1,
            curvV: curvaturaV1,
            setCurvV: setCurvaturaV1,
            label: "Texto superior",
            color: "#00b894",
            position: [0, 0, 0],
          },
          {
            texto: texto2,
            setTexto: setTexto2,
            fuente: fuente2,
            setFuente: setFuente2,
            extrusion: extrusion2,
            setExtrusion: setExtrusion2,
            curvH: curvaturaH2,
            setCurvH: setCurvaturaH2,
            curvV: curvaturaV2,
            setCurvV: setCurvaturaV2,
            label: "Texto inferior (opcional)",
            color: "#0984e3",
            position: [0, -5, 0],
          },
        ].map((item, i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <input
                type="text"
                value={item.texto}
                onChange={(e) => item.setTexto(e.target.value)}
                placeholder={item.label}
                style={{
                  padding: 8,
                  borderRadius: 10,
                  border: "1px solid #ccc",
                  textAlign: "center",
                  fontSize: 16,
                  marginBottom: 6,
                }}
              />

              {/* Extrusión */}
              <label>💎 Extrusión: {item.extrusion.toFixed(1)}</label>
              <input
                type="range"
                min="0.5"
                max="10"
                step="0.1"
                value={item.extrusion}
                onChange={(e) => item.setExtrusion(Number(e.target.value))}
              />

              {/* Curvatura Horizontal */}
              <label>↔️ Curvatura Horizontal: {item.curvH.toFixed(2)} (rango -1 .. 1)</label>
              <input
                type="range"
                min="-1"
                max="1"
                step="0.01"
                value={item.curvH}
                onChange={(e) => item.setCurvH(Number(e.target.value))}
              />

              {/* Curvatura Vertical */}
              <label>↕️ Curvatura Vertical: {item.curvV.toFixed(2)} (rango -1 .. 1)</label>
              <input
                type="range"
                min="-1"
                max="1"
                step="0.01"
                value={item.curvV}
                onChange={(e) => item.setCurvV(Number(e.target.value))}
              />
            </div>

            <select
              value={item.fuente}
              onChange={(e) => item.setFuente(e.target.value)}
              size={8}
              style={{
                padding: 8,
                borderRadius: 10,
                border: "1px solid #ccc",
                width: 180,
                height: 150,
                overflowY: "scroll",
              }}
            >
              {Object.keys(fuentes).map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {/* Vista previa 2D */}
      <div
        style={{
          width: 300,
          height: 120,
          border: "1px solid #ddd",
          borderRadius: 12,
          display: "flex",
          flexDirection: "column",
          justifyContent: mostrarAmbos ? "space-around" : "center",
          alignItems: "center",
          background: "#fafafa",
          fontFamily: "Arial, sans-serif",
          boxShadow: "0 0 10px rgba(0,0,0,0.1)",
          marginBottom: 20,
        }}
      >
        {texto1 && <span style={{ fontSize: 20, fontWeight: "bold" }}>{texto1}</span>}
        {texto2 && <span style={{ fontSize: 18 }}>{texto2}</span>}
      </div>

      {/* Vista 3D */}
      <div
        style={{
          width: ancho,
          height: alto,
          border: "1px solid #ccc",
          borderRadius: 12,
          boxShadow: "0 0 10px rgba(0,0,0,0.1)",
          marginBottom: 10,
        }}
      >
        <Canvas camera={{ position: [0, 0, zoom], fov: 45 }}>
          dpr={[1, 1.5]} // 🔥 limita la resolución máxima  
          frameloop="demand"
          <CameraController zoom={zoom} />
          <color attach="background" args={["#ffffff"]} />
          <ambientLight intensity={0.6} />
          <directionalLight position={[10, 10, 10]} intensity={1} />
          <Suspense fallback={null}>
            <group rotation={[0, rotacionY, 0]} position={[0, mostrarAmbos ? 2 : 0, 0]}>
              {texto1 && (
                <BendableText
                  font={fuentes[fuente1]}
                  size={3}
                  height={extrusion1}
                  curvatureH={curvaturaH1}
                  curvatureV={curvaturaV1}
                  position={[0, 0, 0]}
                  color="#00b894"
                >
                  {texto1}
                </BendableText>
              )}
              {texto2 && (
                <BendableText
                  font={fuentes[fuente2]}
                  size={3}
                  height={extrusion2}
                  curvatureH={curvaturaH2}
                  curvatureV={curvaturaV2}
                  position={[0, -5, 0]}
                  color="#0984e3"
                >
                  {texto2}
                </BendableText>
              )}
            </group>
          </Suspense>
          <OrbitControls enablePan enableRotate enableZoom />
        </Canvas>
      </div>

      {/* Controles extra */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
        <label>🔍 Zoom: {zoom}</label>
        <input type="range" min="-20" max="80" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
        <label>🌀 Rotación: {rotacionY.toFixed(2)}</label>
        <input
          type="range"
          min="-3.14"
          max="3.14"
          step="0.1"
          value={rotacionY}
          onChange={(e) => setRotacionY(Number(e.target.value))}
        />
        <label>📏 Ancho del modelo: {ancho}px</label>
        <input type="range" min="300" max="800" value={ancho} onChange={(e) => setAncho(Number(e.target.value))} />
        <label>📐 Alto del modelo: {alto}px</label>
        <input type="range" min="300" max="800" value={alto} onChange={(e) => setAlto(Number(e.target.value))} />
      </div>
    </div>
  );
}
